import React, { useEffect, useState, useMemo } from "react";
import { View, StyleSheet, ScrollView, Modal, Pressable, Dimensions } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { COLORS, STRINGS } from "../../constants";
import Chip from "../Chip/Chip";
import { faCalendarAlt, faIcons } from "@fortawesome/free-solid-svg-icons";
import EventStore from "../../storage/eventStore";
import CalendarModal from "../CalendarModal/CalendarModal";

const screenWidth = Dimensions.get("window").width;

/**
 * ListViewCommon component displays a list of events.
 * It uses a FlatList to render each event and allows navigation to a details screen on press.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.navigation - The navigation object used to navigate between screens.
 * @param {Object} props.FontAwesomeIcon - The FontAwesomeIcon component to be used in the list.
 * @returns {JSX.Element} The ListViewCommon component that displays the list of events.
 */
const FilterChips = ({ FontAwesomeIcon, iconSize }) => {
  const navigation = useNavigation();
  const selectedCategories = EventStore((state) => state.selectedCategories);
  const selectedFromDate = EventStore((state) => state.selectedFromDate);
  const selectedToDate = EventStore((state) => state.selectedToDate);
  const [showCalendar, setShowCalendar] = useState(false);

  let categoryLabel = STRINGS.category;
  if (Array.isArray(selectedCategories)) {
    const count = selectedCategories.length;
    if (count === 1) {
      categoryLabel = selectedCategories[0];
    } else if (count > 1) {
      categoryLabel = `${selectedCategories[0]} +${count - 1} more`;
    }
  }
  // Format selected date for display
  const dateLabel = useMemo(() => {
    if (!selectedFromDate || !selectedToDate) return STRINGS.allDates;
    const from = new Date(selectedFromDate);
    const to = new Date(selectedToDate);
    if (isNaN(from) || isNaN(to)) return "Any - Any";

    const sameDay = from.toDateString() === to.toDateString();
    const sameYear = from.getFullYear() === to.getFullYear();

    const formatSameYear = { month: "short", day: "numeric" };
    const formatDifferentYear = { month: "short", day: "numeric", year: "numeric" };

    if (sameDay) {
      return from.toLocaleDateString("en-US", sameYear ? formatSameYear : formatDifferentYear);
    }

    return sameYear
      ? `${from.toLocaleDateString("en-US", formatSameYear)} - ${to.toLocaleDateString("en-US", formatSameYear)}`
      : `${from.toLocaleDateString("en-US", formatDifferentYear)} - ${to.toLocaleDateString(
          "en-US",
          formatDifferentYear
        )}`;
  }, [selectedFromDate, selectedToDate]);
  /**
   * Fetches events based on the selected "from" date.
   * If the selected date is earlier than today, it defaults to today's date.
   * Updates the application state with the adjusted date if necessary.
   * Extracts the day and month from the valid date and fetches events for that day.
   */
  const fetchEventsBySelectedFromDate = () => {
    let validDate = selectedFromDate ? new Date(selectedFromDate) : new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (validDate < today) {
      validDate = today;
      EventStore.setState({ selectedFromDate: today });
    }
  };
  /**
   * Fetch events on component mount
   */
  useEffect(() => {
    fetchEventsBySelectedFromDate();
  }, []);
  /**
   * Navigates to FiltersView screen.
   */
  const openFilters = () => {
    navigation.navigate("FiltersView");
  };
  const openCalendar = () => {
    setShowCalendar(!showCalendar);
  };
  /**
   * Renders a single event item in the FlatList.
   */

  return (
    <View style={styles.chipsContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.chipScrollContainer}>
        <Chip
          FontAwesomeIcon={FontAwesomeIcon}
          icon={faCalendarAlt}
          title={dateLabel}
          size={iconSize}
          backgroundColor={COLORS.primary}
          borderColor={COLORS.primary}
          iconColor={COLORS.textLight}
          textColor={COLORS.textLight}
          onPress={openCalendar}
          type="button"
          accessibilityLabel={`${STRINGS.filterBy} ${dateLabel.replace(/-/g, STRINGS.to)}`}
        />
        {showCalendar && (
          <Modal
            visible={showCalendar}
            animationType="fade"
            transparent={true}
            onRequestClose={() => setShowCalendar(false)}
          >
            <Pressable style={styles.modalOverlay} onPress={() => setShowCalendar(false)}>
              <View style={styles.modalContentWrapper} pointerEvents="box-none">
                <Pressable onPress={() => {}} style={styles.modalContent}>
                  <CalendarModal onClose={() => setShowCalendar(false)} />
                </Pressable>
              </View>
            </Pressable>
          </Modal>
        )}
        <Chip
          FontAwesomeIcon={FontAwesomeIcon}
          icon={faIcons}
          title={categoryLabel}
          size={iconSize}
          borderColor={COLORS.primary}
          backgroundColor={selectedCategories.length > 0 ? COLORS.primary : COLORS.backgroundGreen}
          iconColor={selectedCategories.length > 0 ? COLORS.textLight : COLORS.primary}
          textColor={selectedCategories.length > 0 ? COLORS.textLight : COLORS.textDark}
          onPress={openFilters}
          type="button"
          accessibilityLabel={
            categoryLabel === STRINGS.category
              ? `${STRINGS.filterBy} ${STRINGS.category}`
              : `${STRINGS.filteringBy} ${categoryLabel}`
          }
        />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  chipsContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingBottom: 16,
    gap: 8,
  },
  chipScrollContainer: {
    flexDirection: "row",
    gap: 8,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
  },
  modalContentWrapper: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "white",
    padding: 20,
    borderRadius: 8,
    elevation: 5,
    maxWidth: screenWidth * 0.95,
  },
  calendarContainer: {
    alignItems: "center",
  },
});

export default React.memo(FilterChips);
